/** One stack owns keyboard containment, inertness and the document scroll lock. */
type Layer = {
  host: HTMLElement;
  dialog: HTMLElement;
  depth: number;
  initialFocus: () => void;
  close: () => void;
  returnFocus: () => HTMLElement | null;
  opener: HTMLElement | null;
  lastFocus: HTMLElement | null;
  /** Set when the overlay this layer was opened from closes first (e.g. a
   *  dirty-discard prompt over a Modal, both closed by one action). */
  inheritedOpener?: HTMLElement | null;
};
const stack: Layer[] = [];
const backgrounds = new Map<HTMLElement, { inert: boolean; inertAttribute: string | null; hidden: string | null }>();
let root: HTMLElement | null = null;
let overflow = '';
let observer: MutationObserver | null = null;

export function overlayRoot() {
  if (!root?.isConnected) {
    root = document.createElement('div');
    root.className = 'ui-overlay-root';
    document.body.append(root);
  }
  return root;
}

export function focusAvailable(node: HTMLElement | null): node is HTMLElement {
  if (!node?.isConnected || node.matches(':disabled, [aria-disabled="true"], input[type="hidden"]')) return false;
  for (let current: HTMLElement | null = node; current; current = current.parentElement) {
    if (current.hidden || current.hasAttribute('inert') || current.getAttribute('aria-hidden') === 'true') return false;
    const style = getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.getPropertyValue('content-visibility') === 'hidden') return false;
    if (current instanceof HTMLDetailsElement && !current.open) {
      const summary = current.querySelector('summary');
      if (!summary?.contains(node)) return false;
    }
  }
  return true;
}

export function focusElement(node: HTMLElement) {
  const temporary = !node.hasAttribute('tabindex') && node.tabIndex < 0;
  if (temporary) node.setAttribute('tabindex', '-1');
  node.focus();
  if (temporary) node.removeAttribute('tabindex');
}

export function tabbables(container: HTMLElement) {
  return [...container.querySelectorAll<HTMLElement>(
    'button, a[href], area[href], input, select, textarea, summary, iframe, [tabindex], [contenteditable="true"]',
  )].filter((node) => node.tabIndex >= 0 && focusAvailable(node))
    .sort((a, b) => (a.tabIndex > 0 ? a.tabIndex : Infinity) - (b.tabIndex > 0 ? b.tabIndex : Infinity));
}

function restoreBackgrounds() {
  for (const [node, previous] of backgrounds) {
    node.inert = previous.inert;
    if (previous.inertAttribute === null) node.removeAttribute('inert');
    else node.setAttribute('inert', previous.inertAttribute);
    if (previous.hidden === null) node.removeAttribute('aria-hidden');
    else node.setAttribute('aria-hidden', previous.hidden);
  }
  backgrounds.clear();
}

function conceal(node: HTMLElement) {
  backgrounds.set(node, { inert: node.inert, inertAttribute: node.getAttribute('inert'), hidden: node.getAttribute('aria-hidden') });
  node.inert = true;
  node.setAttribute('inert', '');
  node.setAttribute('aria-hidden', 'true');
}

function syncBackgrounds() {
  restoreBackgrounds();
  if (!stack.length) return;
  for (const sibling of document.body.children) {
    if (sibling instanceof HTMLElement && sibling !== root) conceal(sibling);
  }
  for (const layer of stack.slice(0, -1)) conceal(layer.host);
}

function containFocus(layer: Layer) {
  if (focusAvailable(layer.lastFocus) && layer.dialog.contains(layer.lastFocus)) focusElement(layer.lastFocus);
  else layer.initialFocus();
}

function focusIn(event: FocusEvent) {
  const top = stack[stack.length - 1];
  if (!top || !(event.target instanceof HTMLElement)) return;
  if (top.dialog.contains(event.target)) top.lastFocus = event.target;
  else containFocus(top);
}

function keyDown(event: KeyboardEvent) {
  const top = stack[stack.length - 1];
  if (!top || event.defaultPrevented || event.isComposing) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    top.close();
  } else if (event.key === 'Tab') {
    const controls = tabbables(top.dialog);
    const active = document.activeElement;
    const index = controls.indexOf(active as HTMLElement);
    if (!controls.length || index === -1 || (event.shiftKey ? index === 0 : index === controls.length - 1)) {
      event.preventDefault();
      (event.shiftKey ? controls[controls.length - 1] : controls[0])?.focus();
      if (!controls.length) top.dialog.focus();
    }
  }
}

export function isTopOverlay(host: HTMLElement) { return stack[stack.length - 1]?.host === host; }

export function registerOverlay(layer: Layer) {
  const portal = overlayRoot();
  if (!stack.length) {
    overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', keyDown);
    document.addEventListener('focusin', focusIn, true);
    observer = new MutationObserver(syncBackgrounds);
    observer.observe(document.body, { childList: true });
  }
  stack.push(layer);
  stack.sort((a, b) => a.depth - b.depth);
  // Depth also handles nested overlays mounted in the same React commit.
  const next = stack[stack.indexOf(layer) + 1];
  portal.insertBefore(layer.host, next?.host ?? null);
  // Move focus before hiding the old layer from assistive technology.
  restoreBackgrounds();
  stack[stack.length - 1]?.initialFocus();
  syncBackgrounds();
  return () => {
    const wasTop = isTopOverlay(layer.host);
    const index = stack.indexOf(layer);
    if (index !== -1) stack.splice(index, 1);
    // A layer opened from inside this one would otherwise restore focus to
    // a control that is about to disappear: hand it this layer's opener.
    for (const above of stack) {
      if (above.opener && layer.host.contains(above.opener)) {
        above.inheritedOpener = layer.inheritedOpener ?? layer.opener;
      }
    }
    layer.host.remove();
    syncBackgrounds();
    if (!stack.length) {
      observer?.disconnect();
      observer = null;
      document.removeEventListener('keydown', keyDown);
      document.removeEventListener('focusin', focusIn, true);
      document.body.style.overflow = overflow;
      portal.remove();
      root = null;
    }
    if (wasTop) {
      const restore = () => {
        const target = [layer.opener, layer.inheritedOpener ?? null, layer.returnFocus(),
          document.querySelector<HTMLElement>('h1')].find(focusAvailable);
        if (target) focusElement(target);
        else if (stack.length) containFocus(stack[stack.length - 1]!);
      };
      const opener = layer.opener as (HTMLElement & { disabled?: boolean }) | null;
      if (opener?.isConnected && opener.disabled === true) {
        // A busy opener (disabled while its action ran) is re-enabled by the
        // same state change that closes the overlay: re-check next tick,
        // unless focus has already moved somewhere real.
        setTimeout(() => {
          const active = document.activeElement;
          if (!active || active === document.body || active === document.documentElement) restore();
        }, 0);
      } else {
        restore();
      }
    }
  };
}
